import React, { useEffect, useState, useRef } from 'react';
import { useDashboard } from '../DashboardContext';
import { ActivityDashboardService } from '../activityDashboardService';

interface Props {
    timeframe: string;
    setTimeframe: (tf: string) => void;
}

const TIMEFRAMES = [
    { id: 'monthly',    label: 'Monthly'    },
    { id: 'quarterly',  label: 'Quarterly'  },
    { id: 'halfyearly', label: 'Half-Yearly'},
    { id: 'annual',     label: 'Annual'     },
];

const SESSION_GROUP_KEY = 'uad_selected_group';

const InstituteSelectors: React.FC<Props> = ({ timeframe, setTimeframe }) => {
    const { setSelectedGroup, setSelectedInstitute, selectedGroupId, instituteLoaded } = useDashboard();

    const [groups, setGroups]           = useState<any[]>([]);
    const [instSearch, setInstSearch]   = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [instLoading, setInstLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [instituteSelected, setInstituteSelected] = useState(false);
    const [selectedGroupValue, setSelectedGroupValue] = useState(''); // tracks <select> value

    const debounceRef = useRef<any>(null);
    const wrapperRef  = useRef<HTMLDivElement>(null);

    // Load groups on mount; restore persisted group from session
    useEffect(() => {
        ActivityDashboardService.getGroups()
            .then(r => {
                const list = r.data?.data || [];
                setGroups(list);
                // Restore session-persisted group
                const saved = sessionStorage.getItem(SESSION_GROUP_KEY);
                if (saved) {
                    try {
                        const { id, name } = JSON.parse(saved);
                        const match = list.find((g: any) => String(g.id) === String(id));
                        if (match) {
                            setSelectedGroupValue(String(match.id));
                            setSelectedGroup(match.id, match.name || name);
                        }
                    } catch { /* ignore */ }
                }
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Typeahead — fires when user types (only when no institute is selected yet)
    useEffect(() => {
        if (instituteSelected) return;
        clearTimeout(debounceRef.current);
        if (instSearch.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }

        debounceRef.current = setTimeout(async () => {
            setInstLoading(true);
            try {
                const r = await ActivityDashboardService.searchInstitutes(instSearch, selectedGroupId ?? undefined);
                const list = r.data?.data || [];
                console.log('[Institute Search] results:', list);
                setSuggestions(list);
                setShowSuggestions(true);
            } catch (err: any) {
                console.error('[Institute Search] error:', err?.response?.data || err?.message || err);
                setSuggestions([]);
                setShowSuggestions(true); // show "no results" even on error
            } finally {
                setInstLoading(false);
            }
        }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instSearch, selectedGroupId]);

    // Close suggestions on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node))
                setShowSuggestions(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleInputChange = (val: string) => {
        setInstSearch(val);
        setInstituteSelected(false);
        if (!val) setSelectedInstitute(null, '');
    };

    const handleSelectInstitute = (inst: any) => {
        setInstSearch(inst.name);
        setInstituteSelected(true);
        setShowSuggestions(false);
        setSuggestions([]);
        setSelectedInstitute(inst.id, inst.name);
    };

    const handleGroupChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setSelectedGroupValue(val);
        const opt = groups.find((g: any) => String(g.id) === val);
        const id = opt ? opt.id : null;
        const name = opt ? opt.name : '';
        // Persist group in session
        if (id) sessionStorage.setItem(SESSION_GROUP_KEY, JSON.stringify({ id, name }));
        else sessionStorage.removeItem(SESSION_GROUP_KEY);
        setSelectedGroup(id, name);
        // Clear institute when group changes
        setInstSearch('');
        setInstituteSelected(false);
        setSuggestions([]);
    };

    const handleClearInstitute = () => {
        setInstSearch('');
        setInstituteSelected(false);
        setSuggestions([]);
        setSelectedInstitute(null, '');
    };

    return (
        <div className="bg-white border rounded shadow-sm p-3 mb-3">
            <div className="row g-3 align-items-end">

                {/* Group Dropdown */}
                <div className="col-md-3">
                    <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                        <i className="bi bi-buildings me-1"></i>Group
                    </label>
                    <select
                        className="form-select form-select-sm"
                        value={selectedGroupValue}
                        onChange={handleGroupChange}
                    >
                        <option value="">All Groups</option>
                        {groups.map((g: any) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                    </select>
                </div>

                {/* Institute Typeahead */}
                <div className="col-md-4" ref={wrapperRef} style={{ position: 'relative' }}>
                    <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                        <i className="bi bi-mortarboard me-1"></i>Institute
                    </label>
                    <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white">
                            {instLoading
                                ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                                : <i className="bi bi-search text-muted"></i>}
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search institute… (min 2 chars)"
                            value={instSearch}
                            onChange={e => handleInputChange(e.target.value)}
                            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                            autoComplete="off"
                        />
                        {instSearch && (
                            <button className="btn btn-outline-secondary btn-sm" type="button"
                                onClick={handleClearInstitute}>
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Suggestions dropdown */}
                    {showSuggestions && (
                        <div className="border rounded bg-white shadow position-absolute w-100 mt-1"
                            style={{ zIndex: 1050, maxHeight: 300, overflowY: 'auto', top: '100%' }}>
                            {suggestions.length > 0 ? suggestions.map((inst: any) => (
                                <div key={inst.id}
                                    className="px-3 py-2 border-bottom"
                                    style={{ cursor: 'pointer' }}
                                    onMouseDown={e => { e.preventDefault(); handleSelectInstitute(inst); }}>
                                    <div className="fw-semibold font-13">{inst.name}</div>
                                    {inst.group_name && (
                                        <div className="text-muted" style={{ fontSize: 11 }}>{inst.group_name}</div>
                                    )}
                                    {inst.city_name && (
                                        <div className="text-muted" style={{ fontSize: 11 }}>
                                            <i className="bi bi-geo-alt me-1"></i>{inst.city_name}
                                        </div>
                                    )}
                                </div>
                            )) : (
                                <div className="px-3 py-2 text-muted small">
                                    No institutes found.{selectedGroupId ? ' Try a different name or clear the group filter.' : ' Try a different name.'}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Period Chips */}
                <div className="col-md-5">
                    <label className="form-label small fw-semibold text-uppercase text-muted mb-1">
                        <i className="bi bi-calendar3 me-1"></i>Period
                    </label>
                    <div className="btn-group w-100" role="group">
                        {TIMEFRAMES.map(tf => (
                            <button
                                key={tf.id}
                                type="button"
                                className={`btn btn-sm ${timeframe === tf.id ? 'btn-dark active' : 'btn-outline-secondary'}`}
                                disabled={!instituteLoaded}
                                style={{ opacity: !instituteLoaded ? 0.5 : 1, cursor: !instituteLoaded ? 'not-allowed' : 'pointer' }}
                                onClick={() => instituteLoaded && setTimeframe(tf.id)}
                            >
                                {tf.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InstituteSelectors;
